import {
  FormControl,
  type FormControlProps,
  FormHelperText,
  InputLabel,
  type InputLabelProps,
  MenuItem,
  makeStyles,
  Select,
  type SelectProps,
} from "@material-ui/core";
import React from "react";
import HelperText from "./HelperText";

export interface OptionType<
  TValue = string,
  TLabel = string | React.ReactNode,
> {
  label: TLabel;
  value: TValue;
  disabled?: boolean;
}

interface StyleProps {
  customWidth?: string;
}

type CustomSelectProps = {
  "data-testid"?: string;
} & SelectProps;

export type SelectFieldProps = StyleProps &
  Omit<React.ComponentProps<typeof Select>, "error"> & {
    customWidth?: string;
    formControlProps?: FormControlProps;
    /**
     * Force the helper row not to be displayed.
     * This will suppress the any errors from being shown but will set the height of the component to be only
     * the height of the input; rather than including whitespace for the error/helper message to render in.
     */
    hideHelper?: boolean;
    helperText?: React.ReactNode;
    inputLabelProps?: InputLabelProps;
    name: string;
    options?: OptionType[];
    selectProps?: CustomSelectProps;
    variant?: FormControlProps["variant"];
    error?: string;
  };

const useStyles = makeStyles(() => ({
  root: {
    // `??` — the old `customWidth || fullWidth ? ... : ...` grouping
    // collapsed every customWidth value to "100%".
    width: (props: SelectFieldProps) =>
      props.customWidth ?? (props.fullWidth ? "100%" : "max-content"),
  },
  // The theme's 16px-top / 8px-bottom input padding leaves room for the
  // inside-floating label; without a label the value sits low in the box.
  // Same 24px total, so no height change — just recentred.
  unlabelledSelect: {
    paddingTop: 12,
    paddingBottom: 12,
  },
}));

const SelectField = React.forwardRef<HTMLInputElement, SelectFieldProps>(
  (props, ref) => {
    const {
      children = undefined,
      formControlProps = {},
      name,
      inputLabelProps = {},
      options = undefined,
      label = undefined,
      required = false,
      fullWidth = false,
      helperText = undefined,
      hideHelper,
      selectProps,
      id = name,
      variant = "outlined",
      error = undefined,
      disabled = false,
      customWidth = undefined,
      ...delegateProps
    } = props;

    const labelId = inputLabelProps.id ?? `${id}-label`;

    // See https://material-ui.com/components/selects/#accessibility
    const a11yInputLabelProps = {
      id: labelId,
      htmlFor: id,
      ...inputLabelProps,
    };

    const classes = useStyles({ customWidth, ...props });

    return (
      <FormControl
        {...formControlProps}
        error={!!error}
        fullWidth={fullWidth}
        required={required}
        variant={variant}
        disabled={disabled}
      >
        {label && <InputLabel {...a11yInputLabelProps}>{label}</InputLabel>}
        <Select
          inputProps={{
            id,
            name,
            "data-testid": "select-input",
          }}
          classes={{
            root: classes.root,
            ...(label ? {} : { select: classes.unlabelledSelect }),
          }}
          labelId={labelId}
          {...selectProps}
          value={selectProps?.value ?? ""}
          {...delegateProps}
          ref={ref}
        >
          {options?.map((option) => {
            return (
              <MenuItem
                data-testid={`select-option-${option.value}`}
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </MenuItem>
            );
          })}
          {children}
        </Select>
        {hideHelper ? null : (
          <FormHelperText>
            <HelperText error={error}>{helperText}</HelperText>
          </FormHelperText>
        )}
      </FormControl>
    );
  },
);

SelectField.displayName = "SelectField";

export default SelectField;
